<!--
  @component ValidationIO — control-plane input sanitization lens.
  chain:  useSecurityIO().control.validation → this → ctx.validation
  shape:  PURE + in-process. Instant client-side defense-in-depth — sanitize
          UI input (project names, image refs, SQL, paths, …) BEFORE it leaves
          the browser. The deployment server re-validates authoritatively.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { VALIDATION_IO_KEY } from "./security-io.js";
  import type { ValidationIOContext } from "./security-io.js";

  export { VALIDATION_IO_KEY };
  export type { ValidationIOContext };

  export function useValidationIO(): ValidationIOContext {
    const ctx = getContext<ValidationIOContext | undefined>(VALIDATION_IO_KEY);
    if (!ctx) throw new Error("useValidationIO() called outside of a ValidationIO tree.");
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useSecurityIO } from "./SecurityIO.svelte";
  import type { ValidationCheck } from "./security-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useSecurityIO();

  const ctx: ValidationIOContext = {
    get ready() { return parent.control !== null; },
    sanitizeHtml(input) { return parent.control?.validation.sanitizeHtml(input) ?? input; },
    sanitizeSql(input) { return parent.control?.validation.sanitizeSql(input) ?? input; },
    sanitizePath(input) { return parent.control?.validation.sanitizePath(input) ?? input; },
    validateStructure(value, maxDepth = 12) {
      const r = parent.control?.validation.validateStructure(value, maxDepth);
      return r?.success === true;
    },
    check(input, kind): ValidationCheck {
      const v = parent.control?.validation;
      const sanitized = !v
        ? input
        : kind === "html"
          ? v.sanitizeHtml(input)
          : kind === "sql"
            ? v.sanitizeSql(input)
            : v.sanitizePath(input);
      return { ok: sanitized === input, sanitized };
    },
  } satisfies ValidationIOContext;

  setContext(VALIDATION_IO_KEY, ctx);
</script>

{@render children?.()}
